import { useCallback } from 'react';

import {
  AudioRecorder,
  LanguageSelect,
  MicrophoneSelector,
  TemplateSelect,
} from '@repo/videa-notes';

import {
  mixpanelEvents,
  useTrackEventWithOptionalParams,
} from '@/utils/mixpanel';

import { useVideaNotesCoreContext } from '../../context/VideaNotesCoreContext';
import {
  useRecorderState,
  useRecordingHandlers,
  useTemplateSelection,
} from '../../context/VideaNotesRecordingContext';

/**
 * Audio capture header component.
 * Shows microphone selector and prompt/template selection in the header area.
 */
export const AudioCaptureHeader = () => {
  const { recorderStatus, mediaBlobUrl } = useRecorderState();

  const {
    templateOptions,
    selectedTemplate,
    supportedLanguages,
    selectedLanguage,
    onSelectedLanguage,
    onSelectedTemplate,
  } = useTemplateSelection();

  const { onMonitoringStreamChange } = useRecordingHandlers();
  const { trackEvent } = useTrackEventWithOptionalParams();

  const handlePreferredMicrophoneCheck = useCallback(
    (microphoneName: string | null) => {
      trackEvent(mixpanelEvents.videaNotesPreferredMicrophoneCheck, {
        preferredMicrophoneFound: microphoneName !== null,
        microphoneName,
      });
    },
    [trackEvent],
  );

  const isRecorderStopped =
    recorderStatus === 'stopped' || recorderStatus === 'idle';

  // Don't show header controls if we have a recorded blob
  if (mediaBlobUrl) {
    return null;
  }

  return (
    <div className="vai-flex vai-justify-end vai-w-full vai-gap-1">
      <div className="vai-w-1/2 vai-pr-1">
        <MicrophoneSelector
          onMonitoringStreamChange={onMonitoringStreamChange}
          onPreferredMicrophoneCheck={handlePreferredMicrophoneCheck}
        />
      </div>
      {isRecorderStopped && (
        <>
          <div className="vai-w-1/2 vai-pr-1">
            <TemplateSelect
              templateOptions={templateOptions}
              selectedTemplate={selectedTemplate}
              onSelectedTemplate={onSelectedTemplate}
            />
          </div>
          <LanguageSelect
            supportedLanguages={supportedLanguages}
            selectedLanguage={selectedLanguage}
            onSelectedLanguage={onSelectedLanguage}
          />
        </>
      )}
    </div>
  );
};

/**
 * Audio capture body component.
 * Shows the audio recorder controls and user instructions for procedure templates.
 */
export const AudioCaptureBody = () => {
  const { patient, patientName, isVoiceNotesConsentEnabled } =
    useVideaNotesCoreContext();

  const {
    selectedTemplateName,
    selectedTemplateUserInstructions,
    isAudioUploadEnabled,
    showDevTools,
  } = useTemplateSelection();

  const {
    onCancelRecording,
    onCancelRecordingConfirmation,
    onPauseRecording,
    onResumeRecording,
    onStartRecording,
    onStartProviderOnlyRecording,
    onGenerateNote,
    onPatientConsentDenied,
    onPermissionOutcome,
  } = useRecordingHandlers();

  return (
    <div className="vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-w-full vai-overflow-hidden vai-items-center vai-py-2">
      <AudioRecorder
        patientName={patientName}
        patientId={patient?.pmsPatientId ?? 'N/A'}
        templateName={selectedTemplateName}
        templateUserInstructions={selectedTemplateUserInstructions}
        isTemplateSelected={selectedTemplateName !== null}
        isVoiceNotesConsentEnabled={isVoiceNotesConsentEnabled}
        isAudioUploadEnabled={isAudioUploadEnabled}
        showDevTools={showDevTools}
        onCancelRecording={onCancelRecording}
        onCancelRecordingConfirmation={onCancelRecordingConfirmation}
        onPauseRecording={onPauseRecording}
        onResumeRecording={onResumeRecording}
        onStartRecording={onStartRecording}
        onStartProviderOnlyRecording={onStartProviderOnlyRecording}
        onGenerateNote={onGenerateNote}
        onPatientConsentDenied={onPatientConsentDenied}
        onPermissionOutcome={onPermissionOutcome}
      />
    </div>
  );
};
